import {
    ChildTextContentElement,
    TextContentElement,
} from '@deltek/specification-client/core/Models/textContentElement'
import clsx from 'clsx'
import {
    PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES,
    PART_TYPES,
} from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { Key } from 'react'
import type { ContentItemViewNodesType, DataNode } from '../types'

/** Cached Firefox detection */
export const isFirefox = navigator.userAgent.toLowerCase().includes('firefox')

/**
 * Type guard to validate DataNode structure
 * @param {any} node - The object to validate
 * @returns {boolean} Whether the input is a valid DataNode
 * @description Ensures the object has required properties with correct types
 */
export const isValidDataNode = (node: any): node is DataNode => {
    return (
        node !== null &&
        typeof node === 'object' &&
        'key' in node &&
        typeof node.key === 'string' &&
        'contentItemNodes' in node &&
        Array.isArray(node.contentItemNodes) &&
        'type' in node &&
        Object.values(NODE_TYPES).includes(node.type) &&
        'order' in node &&
        typeof node.order === 'number'
    )
}

/** Get the label of the active tab */
export const getActiveTabLabel = (tabIndex: number) =>
    Object.values(PART_TYPES)[tabIndex - 1]

/**
 * Find and recursively updates the target tree node's children and toggle its expanded state
 * @param treeNodes - Array of DataNodes to traverse
 * @param targetNode - The key of the parent node to update
 * @param newChildren - Optional new children to replace existing children
 * @returns A new tree data structure with the specified node updated
 */
export const toggleExpandedRecursively = (
    treeNodes: DataNode[],
    targetNode: Key,
    newChildren: DataNode[] = []
): DataNode[] => {
    if (!Array.isArray(treeNodes) || !isValidDataNode(treeNodes[0])) {
        console.error('Invalid treeNode input. Expected an array of DataNodes.')
        return []
    }

    // deep clone to prevent mutation
    const oldTree = JSON.parse(JSON.stringify(treeNodes)) as DataNode[]

    const buildChildNodes = (nodes: DataNode[]): DataNode[] =>
        nodes.map((node) => {
            // if target node found, update its children and expanded state
            if (node.key === targetNode) {
                return {
                    ...node,
                    children: newChildren,
                    expanded: !node.expanded, //toggle expanded state
                }
            }
            // or, recursively update children if they exist
            if (node.children) {
                return {
                    ...node,
                    children: buildChildNodes(node.children),
                }
            }
            // else, return node unchanged if no match found
            return node
        })

    const updatedTree = buildChildNodes(oldTree)

    // check if target node was found by comparing the updated tree
    const wasNodeFound = JSON.stringify(updatedTree) !== JSON.stringify(oldTree)
    if (!wasNodeFound) {
        console.warn(`Target node '${String(targetNode)}' not found in tree`)
    }

    return updatedTree
}

/**
 * Finds node keys that have children recursively
 * @param nodes - Array of data nodes
 * @returns Array of keys with children
 */
export const findWithChild = (nodes: DataNode[]): Key[] => {
    const keys: Key[] = []
    const traverseChildren = (node: DataNode) => {
        // Include nodes that have children OR manufacturer/BOD header paragraphs
        // (isManufacturers or isBOD with dtvContentItemParentId === null). Headers with no
        // pills must still be expanded so the injected Product Library button is visible.
        // Use the primary dtvContentItemParentId==null check rather than the full
        // isManufacturerOrBODHeader (which has a text-fallback that can return true for
        // BOD pills whose text starts with a placeholder keyword like "Product") (Bug 2688614).
        if (node.children?.length || ((node.isManufacturers || node.isBOD) && node.contentItemView?.dtvContentItemParentId == null)) {
            keys.push(node.key)
            for (const child of node.children || []) {
                traverseChildren(child)
            }
        }
    }
    nodes.forEach(traverseChildren)
    return keys
}

/**
 * Get all keys of DataNodes recursively
 * @param nodes - Array of data nodes
 * @returns - Array of all keys
 */
export const getAllKeys = (nodes: DataNode[] = []): string[] => {
    let keys: string[] = []
    nodes.forEach((node) => {
        keys.push(node.key) // add the current node's key

        if (node.children) {
            keys = keys.concat(getAllKeys(node.children)) // add keys from children recursively
        }
    })
    return keys
}

/**
 * Get article IDs that are still loading (not yet expanded in the tree)
 * @param treeNodes - Tree nodes to check against
 * @param expandedKeys - Keys of expanded nodes
 * @param articlesLoading - Array of article IDs currently loading
 * @returns Article IDs that are still loading (not expanded)
 */
export const getUpdatedLoadingArticles = (
    treeNodes: DataNode[],
    expandedKeys: Key[],
    articlesLoading: string[]
): string[] => {
    const expandedSet = new Set(expandedKeys)
    const expandedArticles = new Set<string>()

    // Recursively find expanded articles
    const findExpandedArticles = (nodes: DataNode[]) => {
        for (const node of nodes) {
            if (node.type === 'Article' && expandedSet.has(node.key)) {
                expandedArticles.add(node.key as string)
            }
            node.children && findExpandedArticles(node.children)
        }
    }

    findExpandedArticles(treeNodes)
    return articlesLoading.filter((id) => !expandedArticles.has(id))
}

/**
 * Merges the loading article IDs with the root article content item IDs
 * @param articlesLoading - Array of article IDs that are loading
 * @param rootArticleContentItemIds - Array of root article content item IDs
 * @returns - Array of unique article IDs that are loading
 */
export const mergeLoadingArticleIds = (
    articlesLoading: string[],
    rootArticleContentItemIds?: string[]
): string[] => {
    return Array.from(
        new Set([...articlesLoading, ...(rootArticleContentItemIds || [])])
    ).filter((key) => key !== 'default')
}

/**
 * Find articles that are not loaded in the tree given the following conditions:
 * - Article is not found in the Content Item Nodes
 * - Article's children are not found in the Content Item Nodes
 * @param contentItemNodeIds - Array of Content Item Node IDs
 * @param articleContentItemChildrenIds - Map of Article ID to its children IDs
 * @returns - Array of Article ID not loaded
 */
export const findArticlesNotLoaded = (
    contentItemNodeIds: string[],
    articleContentItemChildrenIds: Map<string, Key[]>
) => {
    const contentItemIdsSet = new Set(contentItemNodeIds)

    // find the articles that don't have their children loaded
    const notLoadedArticleIds: string[] = []
    articleContentItemChildrenIds.forEach((childrenIds, articleId) => {
        // If the article is not loaded, we must fetch it, no need to check the children
        const isArticleLoaded = articleContentItemChildrenIds.has(articleId)

        if (!isArticleLoaded) {
            notLoadedArticleIds.push(articleId)
            return
        }

        if (isArticleLoaded && childrenIds.length === 0) {
            notLoadedArticleIds.push(articleId)
        }

        // If article's children is not loaded, we must fetch it
        const isChildrenLoaded = childrenIds.every((childId) =>
            contentItemIdsSet.has(childId.toString())
        )
        if (!isChildrenLoaded) {
            notLoadedArticleIds.push(articleId)
        }
    })

    return notLoadedArticleIds
}

/**
 * Merges two arrays of content items by applying updates.
 * @param existingItems - The current array of content items.
 * @param updatedItems - The array of content items containing updates.
 * @param overwrite - When true, only include items that have an update; otherwise, merge updates with existing items.
 * @returns The merged array of content items.
 */
export const mergeContentItems = (
    existingItems: Partial<ContentItemViewNodesType>[],
    updatedItems: Partial<ContentItemViewNodesType>[],
    overwrite: boolean = false
): Partial<ContentItemViewNodesType>[] => {
    // create a lookup map of updated items keyed by id
    const updatesById = new Map(
        updatedItems.filter((item) => item.id).map((item) => [item.id, item])
    )
    // overwrite = false, retain all items (updating those that have a corresponding update).
    // overwrite = true, only retain items that have a corresponding update
    const mergedExistingItems = existingItems
        .filter((item) => (overwrite ? updatesById.has(item.id!) : true))
        .map((item) =>
            item.id && updatesById.has(item.id)
                ? updatesById.get(item.id)!
                : item
        )
    // find updated items that are new
    const existingIds = new Set(existingItems.map((item) => item.id))
    const additionalUpdatedItems = updatedItems.filter(
        (item) => item.id && !existingIds.has(item.id)
    )
    return [...mergedExistingItems, ...additionalUpdatedItems]
}

/**
 * Creates a debounced version of a function that delays its execution until after `timeout` milliseconds.
 * The returned function includes a `.cancel()` method to clear any pending timeout.
 *
 * @template T The type of the function to debounce.
 * @param {T} func The function to debounce.
 * @param {number} [timeout=100] The delay in milliseconds.
 * @returns {DebouncedFunction<T>} The debounced function with an attached `.cancel()` method.
 */
// Define a type for the debounced function with a cancel method property
type DebouncedFunction<T extends (...args: any[]) => void> = T & {
    cancel: () => void
}

export const withDebounce = <T extends (...args: any[]) => void>(
    func: T,
    timeout = 100
): DebouncedFunction<T> => {
    let timer: ReturnType<typeof setTimeout> | undefined

    // The core debounced function logic
    const debouncedFunc = ((...args: Parameters<T>) => {
        // Clear existing timer if function is called again before timeout
        if (timer) {
            clearTimeout(timer)
        }
        timer = setTimeout(() => {
            func(...args)
            timer = undefined // Reset timer after execution
        }, timeout)
    }) as DebouncedFunction<T> // Cast the function to include the .cancel method type

    // Attach the cancel method to the debounced function
    debouncedFunc.cancel = () => {
        // Clear the timeout if it exists
        if (timer) {
            clearTimeout(timer)
            timer = undefined
        }
    }

    return debouncedFunc
}

/**
 * Converts a string to PascalCase.
 *
 * @param input - The input string to convert to PascalCase.
 * @returns A string in PascalCase.
 */
export const toPascalCase = (input: string | undefined) => {
    if (!input) return ''

    return (
        input
            // replace underscores and hyphens with spaces
            .replace(/[_-]+/g, ' ')
            .split(' ')
            .filter((word) => word.length > 0)
            // Convert each word: first letter uppercase, rest lowercase
            .map(
                (word) =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            )
            .join('')
    )
}

/**
 * Resolves an object's properties based on specific rules:
 * - Replaces `null` or `undefined` values with an empty string (`''`).
 * - Keeps `false` boolean values as `false`.
 * - Leaves all other values unchanged.
 *
 * @template T - The type of the object.
 * @param {T | Partial<T>} element - The object to resolve.
 * @returns {T} A new object with resolved property values.
 */
export const nullOrUndefinedResolver = <T extends Record<string, any>>(
    element: T | Partial<T>
): T => {
    if (!element) return {} as T

    return Object.entries(element).reduce((acc, [key, value]) => {
        let resolvedValue: any

        if (value === null || value === undefined) {
            resolvedValue = ''
        } else if (value === false) {
            resolvedValue = false
        } else {
            resolvedValue = value
        }

        return {
            ...acc,
            [key]: resolvedValue,
        }
    }, {} as T)
}

/**
 * Replaces an element in the target array with a new value.
 * Supports replacing a single object or an array of objects.
 *
 * @param target - The original array of elements.
 * @param targetId - The ID of the element to replace.
 * @param replacement - A single object or an array of objects to replace the element.
 * @returns A new array with the replacement applied.
 */
export const replaceInArray = <T extends Partial<{ id: string }>>(
    target: T[] = [],
    targetId: string,
    replacement: T | T[]
): T[] => {
    const index = target.findIndex((obj) => obj.id === targetId)

    if (index === -1) return target

    let newArray = [...target]

    if (Array.isArray(replacement)) {
        newArray.splice(index, 1, ...replacement)
    } else {
        newArray[index] = replacement
    }
    return newArray
}

// Re-export manufacturer/BOD utilities from dedicated module
export {
    isManufacturerOrBODHeader,
    isManufacturerOrBODPill,
} from './manufacturerBodUtils'

/**
 * Checks if the given element is an editable field (input, textarea, or contentEditable).
 * @param element The target HTMLElement
 * @returns true if the element is editable, false otherwise
 */
export const isEditableElement = (element: HTMLElement): boolean => {
    const tag = element.tagName
    return tag === 'INPUT' || tag === 'TEXTAREA' || element.isContentEditable
}

/**
 * Gets the appropriate CSS class based on element status and properties.
 * Used for styling elements in NodeContentRenderer and OptionGroup components.
 * @param inactive Whether the element is inactive
 * @param changeType The type of change applied to the element
 * @param areTrackChangesVisible Whether track changes are visible
 * @param styleClasses Object containing the CSS classes from useTreeViewStyles
 * @returns The appropriate CSS class
 */
export const getStatusClass = (
    inactive: boolean,
    changeType: string,
    areTrackChangesVisible: boolean,
    styleClasses: {
        inactiveText: string
        addedText: string
        removedText: string
    }
): string => {
    if (inactive) return clsx(styleClasses.inactiveText, 'inactiveText')
    if (!areTrackChangesVisible) return ''
    if (changeType === 'ADDED') return styleClasses.addedText
    if (changeType === 'DELETED') return styleClasses.removedText
    return ''
}

// Constants - prefix used for element placeholder after keys and inbetween pills
export const ADDITIONAL_ELEMENT_SUFFIX = '_additional'

/**
 * Gets the searchable element ID by removing the additional suffix if present.
 * Used for finding elements in mappings where the original ID (without suffix) is stored.
 *
 * @param elementId - The element ID that may contain the additional suffix
 * @returns The clean element ID suitable for searching in content mappings
 *
 * @example
 * getSearchableElementId('element123_additional') // returns 'element123'
 * getSearchableElementId('element456') // returns 'element456'
 */
export const getSearchableElementId = (elementId: string): string => {
    return elementId?.includes(ADDITIONAL_ELEMENT_SUFFIX)
        ? elementId.replace(ADDITIONAL_ELEMENT_SUFFIX, '')
        : elementId
}

/**
 * Normalizes text content by replacing empty or whitespace-only text with an empty string.
 * This ensures consistent handling of empty text elements across the application.
 *
 * @param element - The text content element to normalize
 * @returns The element with normalized text property
 */
export const normalizeTextContent = (
    element: Partial<ChildTextContentElement>
): Partial<ChildTextContentElement> => {
    const hasEmptyText = !element?.text?.trim()
    return hasEmptyText ? { ...element, text: '' } : element
}

/**
 * Extracts the base element ID from an element that may have additional suffix.
 * Used to get the original element ID when working with derived elements.
 *
 * @param elementId - The element ID that may contain the additional suffix
 * @returns The base element ID without the suffix, or the original ID if no suffix is present
 */
export const getBaseElementId = (
    elementId: string | undefined
): string | undefined => {
    if (!elementId) return elementId
    return elementId.includes(ADDITIONAL_ELEMENT_SUFFIX)
        ? elementId.split(ADDITIONAL_ELEMENT_SUFFIX)[0]
        : elementId
}

/**
 * Filters out duplicate nodes using a Set to track unique keys.
 * Used in tree data transformation to ensure consistent node arrays for numbering calculations.
 * This prevents numbering gaps when API returns duplicate order values.
 *
 * @param nodes - Array of DataNodes that may contain duplicates
 * @param nodeKeys - Set to track unique node keys (mutated by this function)
 * @returns Array of unique nodes with duplicates filtered out
 */
export const filterUniqueNodes = (
    nodes: DataNode[],
    nodeKeys: Set<string>
): DataNode[] => {
    return nodes.filter((node) => {
        if (nodeKeys.has(node.key)) return false
        nodeKeys.add(node.key)
        return true
    })
}

/**
 * Filters nodes based on visibility settings and returns arrays for both ordering calculations and display.
 * This ensures array alignment between numbering calculations and rendered nodes to prevent index mismatches.
 *
 * @param uniqueNodes - Array of unique nodes (no duplicates)
 * @param isInactiveContentVisible - Whether to show hidden/inactive nodes
 * @returns Object containing visibleNodesForOrdering and nodesToDisplay arrays
 */
export const getFilteredNodeArrays = (
    uniqueNodes: DataNode[],
    isInactiveContentVisible: boolean
): {
    visibleNodesForOrdering: DataNode[]
    nodesToDisplay: DataNode[]
} => {
    // Array for consistent numbering calculation - only visible nodes
    const visibleNodesForOrdering = uniqueNodes.filter((node) => !node.isHidden)

    // Array for actual rendering based on visibility settings
    const nodesToDisplay = isInactiveContentVisible
        ? uniqueNodes // Show all (including hidden nodes)
        : visibleNodesForOrdering // Show only visible nodes

    return { visibleNodesForOrdering, nodesToDisplay }
}

/**
 * Recursively checks if an element or its nested elements contain the target ID.
 * @param element - The element to check.
 * @param targetId - The ID to search for.
 * @returns True if the element or its nested elements contain the target ID.
 */
export const hasElementWithId = (
    element: Partial<TextContentElement>,
    targetId: string
): boolean => {
    // Check if current element has the target ID
    if (element.id === targetId) {
        return true
    }

    // Recursively check nested elements
    if (element.elements && element.elements.length > 0) {
        return element.elements.some((nestedElement) =>
            hasElementWithId(nestedElement, targetId)
        )
    }

    return false
}

/**
 * Helper function to get the appropriate disabled styling classes based on priority
 *
 * @param disabled - Whether the component is explicitly disabled (product not added to project)
 * @param inactive - Whether the component is inactive (strike-through)
 * @param editable - Whether the component is editable
 * @param styles - The styles object from useOptionGroupStyles()
 * @returns Combined CSS class string with appropriate disabled/inactive styling
 *
 * Styling precedence (evaluated in order):
 * 1. inactive=true (parent paragraph deactivated) → disabledOptionGroup + disabledBoxedInputContainer (with line-through)
 * 2. disabled=true OR !editable (not added to project / read-only) → notDisabledBoxedInputContainer + disabledOptionGroup (green border, non-interactive)
 * 3. Otherwise (fully enabled) → notDisabledBoxedInputContainer
 */
export const getDisabledStyleClasses = (
    disabled: boolean,
    inactive: boolean,
    editable: boolean,
    styles: {
        disabledOptionGroup: string
        disabledBoxedInputContainer: string
        notDisabledBoxedInputContainer: string
    }
): string => {
    if (inactive) {
        return clsx(
            styles.disabledOptionGroup,
            styles.disabledBoxedInputContainer
        )
    }

    // Not added to project OR read-only — green border but non-interactive
    if (disabled || !editable) {
        return clsx(styles.notDisabledBoxedInputContainer, styles.disabledOptionGroup)
    }

    return styles.notDisabledBoxedInputContainer
}
